import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FieldContainer,
  FormError,
  InputHeader,
  Spacer,
  StyledCheckbox,
  StyledInput,
} from "../../styles/CommonElements";
import styled from "styled-components";
import { AuthContext } from "../../context/AuthProvider";
import RevealPassword from "../RevealPassword";
import { Link } from "react-router-dom";
import Button from "../Button";
import OrDivider from "../OrDivider";
import { APP_TERMS_ROUTE } from "../../helpers/routeNames";
import { formatEmailToLowercase } from "../../utils/emailUtils";

const passwordLowerThreshold = 6;
const passwordUpperThreshold = 15;

const AccountCreationForm = ({
  isThirdPartyUser,
  /** Free Gateway (?plan=free): no password fields; parent supplies a generated password. */
  skipPassword = false,
  isSubmitting,
  onSubmit,
  inviteCode,
  inviteData,
  inviteLoading,
}) => {
  const { user } = useContext(AuthContext);
  const [passwordRevealed, setPasswordRevealed] = useState(false);
  const [passwordConfirmedRevealed, setPasswordConfirmedRevealed] = useState(
    false
  );
  const passwordDomRef = useRef(null);
  const confirmPasswordDomRef = useRef(null);

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    control,
    formState: { isValid, errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      agreedToTerms: false,
    },
  });

  useEffect(() => {
    if (isThirdPartyUser && user) {
      setValue("email", user.email, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [isThirdPartyUser, setValue, user]);

  // Normally would inline this, but we need to access the ref that hook form uses to track the inputs for RevealPassword
  const passwordOptional = isThirdPartyUser || skipPassword;

  const { ref: rhfPasswordRef, ...passwordInputProps } = register("password", {
    required: passwordOptional ? false : "Please enter a password",
    minLength: passwordOptional
      ? undefined
      : {
          value: passwordLowerThreshold,
          message: `Password must be at least ${passwordLowerThreshold} characters`,
        },
    maxLength: passwordOptional
      ? undefined
      : {
          value: passwordUpperThreshold,
          message: `Password must be less than ${passwordUpperThreshold} characters`,
        },
  });

  const { ref: rhfConfirmPasswordRef, ...confirmPasswordInputProps } = register(
    "passwordConfirm",
    {
      required: passwordOptional ? false : "Please confirm your password",
      validate: passwordOptional
        ? undefined
        : (value) => watch("password") === value || "Passwords do not match",
    }
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Invite information display */}
        {inviteCode && (
          <InviteInfoContainer>
            {inviteLoading ? (
              <InviteText>Validating invite code...</InviteText>
            ) : inviteData?.valid ? (
              <InviteText success>
                You've been invited by{" "}
                <strong>{inviteData.referrerName}</strong>!
              </InviteText>
            ) : inviteData?.expired ? (
              <InviteText error>⚠️ This invite code has expired</InviteText>
            ) : inviteData?.valid === false ? (
              <InviteText error>❌ Invalid invite code</InviteText>
            ) : null}
          </InviteInfoContainer>
        )}

        <InputHeader htmlFor="email">Email</InputHeader>
        <InputContainer>
          <StyledInput
            id="email"
            placeholder="you@email.com"
            disabled={isThirdPartyUser}
            className={isThirdPartyUser && "locked"}
            type="email"
            {...register(
              "email",
              // These inline validations can be replaced by Zod
              {
                required: "Please enter your email address",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: "Invalid email address",
                },
              }
            )}
            onChange={(e) => {
              const formattedEmail = formatEmailToLowercase(e.target.value);
              if (formattedEmail !== e.target.value) {
                setValue("email", formattedEmail);
              }
            }}
          />
          {isThirdPartyUser && <Checkmark src="/icons/checkmark.svg" />}
        </InputContainer>
        {errors.email && <FormError>{errors.email.message}</FormError>}
        <Spacer $marginTop="1rem" />

        {!isThirdPartyUser && !skipPassword && (
          <>
            <InputHeader htmlFor="password">Password</InputHeader>
            <InputContainer>
              <StyledInput
                {...passwordInputProps}
                id="password"
                className="transition-colors"
                placeholder={`Must be at least ${passwordLowerThreshold} characters`}
                maxLength={40}
                type={passwordRevealed ? "text" : "password"}
                ref={(el) => {
                  passwordDomRef.current = el;
                  rhfPasswordRef(el);
                }}
              />
              <RevealPassword
                isRevealed={passwordRevealed}
                toggleRevealed={() => setPasswordRevealed(!passwordRevealed)}
                passwordRef={passwordDomRef}
              />
            </InputContainer>
            {errors.password && (
              <FormError>{errors.password.message}</FormError>
            )}

            <Spacer $marginTop="1rem" />

            <InputHeader htmlFor="password-confirm">
              Confirm password
            </InputHeader>
            <InputContainer>
              <StyledInput
                {...confirmPasswordInputProps}
                id="password-confirm"
                className="transition-colors"
                placeholder="Type your password again"
                maxLength={40}
                type={passwordConfirmedRevealed ? "text" : "password"}
                ref={(el) => {
                  confirmPasswordDomRef.current = el;
                  rhfConfirmPasswordRef(el);
                }}
              />
              <RevealPassword
                isRevealed={passwordConfirmedRevealed}
                toggleRevealed={() =>
                  setPasswordConfirmedRevealed(!passwordConfirmedRevealed)
                }
                passwordRef={confirmPasswordDomRef}
              />
            </InputContainer>
            {errors.passwordConfirm && (
              <FormError>{errors.passwordConfirm.message}</FormError>
            )}

            <Spacer $marginTop="1rem" />
          </>
        )}

        <FieldContainer style={{ flexDirection: "row", gap: "0.75rem" }}>
          {/* Controller is a helper component from hook form made to deal with external library (like antd) controlled components */}
          <Controller
            name="agreedToTerms"
            control={control}
            rules={{
              validate: (v) =>
                v === true ||
                "You must agree to the terms of service before registering",
            }}
            render={({ field }) => (
              <StyledCheckbox checked={field.value} {...field} />
            )}
          />

          <TermsOfService>
            I have read and agree to the{" "}
            <StyledLink to={APP_TERMS_ROUTE} target="_blank">
              Terms of Service
            </StyledLink>
            .
          </TermsOfService>
        </FieldContainer>
        {errors.agreedToTerms && (
          <FormError>{errors.agreedToTerms.message}</FormError>
        )}

        <Spacer $marginTop="1.25rem" />

        <Button
          type="submit"
          size="large"
          isLoading={isSubmitting}
          disabled={!isValid || isSubmitting}
        >
          Create Account
        </Button>
      </form>
      {!isThirdPartyUser && (
        <>
          {/*
          <Spacer $marginTop="1rem" />
          <OrDivider />
          */}
          <Spacer $marginTop="1rem" />
          {/*
          <ThirdPartyAuth cta="Sign up" />
          */}
        </>
      )}
    </>
  );
};

const InputContainer = styled.div`
  position: relative;
  text-align: left;

  .locked {
    color: var(--seafoam);
  }
`;

const Checkmark = styled.img`
  right: 14px !important;
  top: 35%;
  height: 12px;
  position: absolute;
`;

const StyledLink = styled(Link)`
  color: rgba(0, 0, 0, 0.75);
`;

const TermsOfService = styled.div`
  color: var(--color-gray-50);
  margin: auto;
  margin-left: 0;
`;

const InviteInfoContainer = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-medium);
`;

const InviteText = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;

  ${(props) =>
    props.success &&
    `
    color: var(--color-success);
  `}

  ${(props) =>
    props.error &&
    `
    color: var(--color-error);
  `}
  
  &:not(:is(${(props) => props.success}, ${(props) => props.error})) {
    color: var(--color-text);
  }
`;

export default AccountCreationForm;
